import Bowser from 'bowser'

import { validateApplePlatform } from '#/store/services/BrowserGuard/Logic/browserApplePlatform'
import { supportsRegexLookbehind } from '#/store/services/BrowserGuard/Logic/browserFeatureSupport'
import {
  BROWSER_MINIMUM_VERSION,
  SupportedBrowser,
} from '#/store/services/BrowserGuard/Logic/browserMinimumVersion'
import {
  formatMinimumVersionForDisplay,
  formatVersionForDisplay,
  meetsMinimumVersion,
} from '#/store/services/BrowserGuard/Logic/browserVersion'
import { BrowserValidationResult } from '#/store/services/BrowserGuard/types'

/**
 * Retorna a regra da política para o navegador detectado, ou `undefined`
 * quando o navegador não possui versão mínima definida.
 */
function findPolicyRule({ parser }: { parser: Bowser.Parser.Parser }): string | undefined {
  const policyEntries = Object.entries(BROWSER_MINIMUM_VERSION) as [SupportedBrowser, string][]

  return policyEntries.find(([browserKey]) => parser.isBrowser(browserKey, true))?.[1]
}

/**
 * Valida a compatibilidade a partir do user agent.
 *
 * A validação segue esta ordem:
 * 1. Verifica a versão da plataforma Apple quando aplicável.
 * 2. Verifica a versão do navegador quando ele está na política.
 * 3. Libera navegadores que não possuem uma versão mínima definida.
 */
function validateByUserAgent({
  userAgent = navigator.userAgent,
}: {
  userAgent?: string
} = {}): BrowserValidationResult {
  const parser = Bowser.getParser(userAgent)
  const { browser, os } = parser.getResult()

  /**
   * Em iOS/iPadOS e Safari no macOS, a compatibilidade do WebKit
   * é verificada antes da versão do navegador.
   */
  const appleValidation = validateApplePlatform({
    browserName: browser.name,
    browserVersion: browser.version,
    osName: os.name,
    osVersion: os.version,
  })

  if (appleValidation) {
    return appleValidation
  }

  const browserName = browser.name ?? 'Desconhecido'
  const browserVersion = formatVersionForDisplay({ version: browser.version ?? '0' })
  const policyRule = findPolicyRule({ parser })

  /**
   * Navegadores que não estão na política não possuem restrição
   * de versão e, portanto, têm acesso liberado.
   *
   * O mesmo vale quando o Bowser não consegue extrair a versão do user
   * agent: sem versão não há o que comparar, e o teste de lookbehind
   * abaixo continua sendo a barreira real. Não trocar por um bloqueio —
   * seria um falso positivo sem escape em Safari.
   */
  if (!policyRule || !browser.version) {
    return {
      browserName,
      browserVersion,
      isSupported: true,
      osName: os.name,
      osVersion: os.version,
      updateTarget: 'browser',
    }
  }

  return {
    browserName,
    browserVersion,
    isSupported: meetsMinimumVersion({
      policyRule,
      version: browser.version,
    }),
    minimumBrowserName: browserName,
    minimumBrowserVersion: formatMinimumVersionForDisplay({ policyRule }),
    osName: os.name,
    osVersion: os.version,
    updateTarget: 'browser',
  }
}

/**
 * Valida se o navegador e a plataforma são compatíveis com a aplicação.
 * Lookbehind, que é um recurso de regex usado em algumas validações do front-end.
 */
export function validateBrowserCompatibility({
  userAgent = navigator.userAgent,
}: {
  userAgent?: string
} = {}): BrowserValidationResult {
  const validationResult = validateByUserAgent({ userAgent })

  if (supportsRegexLookbehind()) {
    return validationResult
  }

  return {
    ...validationResult,
    isSupported: false,
  }
}
